import React, { useState } from 'react'
import { Card, CardContent, Box, Typography, IconButton, Stack, Chip, Collapse } from '@mui/material'
import { ExpandMore, Edit, Check } from '@mui/icons-material'

/**
 * ProfileSection - Reusable card wrapper for profile sections
 * Features:
 * - Consistent styling with hover effects
 * - Optional collapsible content
 * - Edit button with active state
 * - Completion status indicator
 * - Icon support
 * - Theme-based colors
 */
const ProfileSection = ({
  title,
  icon,
  children,
  editable = false,
  collapsible = false,
  defaultExpanded = true,
  completionStatus, // 'complete' | 'incomplete' | null
  onEdit,
  isEditing = false,
  id, // for anchor links
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const handleToggle = () => {
    if (collapsible) {
      setExpanded(!expanded)
    }
  }

  const handleEditClick = () => {
    if (onEdit) {
      onEdit()
    }
  }

  return (
    <Card
      id={id}
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          borderColor: 'primary.light',
        },
      }}
      elevation={0}
    >
      {/* Card Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2.5,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: collapsible ? 'pointer' : 'default',
        }}
        onClick={collapsible ? handleToggle : undefined}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {icon && (
            <Box
              sx={{
                bgcolor: 'primary.light',
                color: 'primary.main',
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            {completionStatus && (
              <Typography variant="caption" color="text.secondary">
                {completionStatus === 'complete' ? 'All fields completed' : 'Incomplete'}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {completionStatus === 'complete' && (
            <Chip icon={<Check />} label="Complete" size="small" color="success" />
          )}
          {editable && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation() // Prevent collapse toggle when clicking edit
                handleEditClick()
              }}
              sx={{
                bgcolor: isEditing ? 'primary.main' : 'transparent',
                color: isEditing ? 'white' : 'text.secondary',
                '&:hover': {
                  bgcolor: isEditing ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <Edit />
            </IconButton>
          )}
          {collapsible && (
            <IconButton
              size="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
              }}
            >
              <ExpandMore />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Card Content */}
      {collapsible ? (
        <Collapse in={expanded}>
          <CardContent sx={{ p: 3 }}>{children}</CardContent>
        </Collapse>
      ) : (
        <CardContent sx={{ p: 3 }}>{children}</CardContent>
      )}
    </Card>
  )
}

export default ProfileSection
